<template lang="pug">
  .panel-page-white.body-1
    .panel-white.mx-auto
      FormErrorAlert.error-alert.mx-auto(
        heading='Missing Fields'
        :message='message'
        @dismiss="message = ''"
        v-if="message")
      .row
        .col-lg-10
          span.h1 Person Search
        .col-lg-2
          button.btn.btn-light(@click="clearForm()") Clear Form
      .separator

      .text-right.body-1
        span.red *
        |  Indicates required field.
      form(autocomplete="off")
        .form-row
          .col-lg-4
            .form-group
              label(for="purposeCode", required='') Purpose Code
              input.form-control(
                id="purposeCode"
                name="purposeCode"
                v-model="purposeCode"
                @input="purposeCodeTouched = true"
                :class="{'is-invalid': purposeCodeInvalid}")
              FormError(v-if="purposeCodeInvalid", message="Purpose code is required.")
        p.body-1.mt-3 To find records, enter as much detail as possible in one or more of the fields below.
        Expander(heading="Person Information",:show.sync="showPersonInfo")
          .form-row
            .col-lg
              .form-group
                label(for="lastName") Last Name
                input#lastName.form-control(
                  v-model="lastName"
                  :class="{'is-invalid': nameSearchInvalid && !lastName}"
                  @input="lastNameTouched = true")
                FormError(v-if="nameSearchInvalid && !lastName",message="Required for name search.")
            .col-lg
              .form-group
                label(for="firstName") First Name
                input#firstName.form-control(
                  v-model="firstName"
                  :class="{'is-invalid': nameSearchInvalid && !firstName}"
                  @input="firstNameTouched = true")
                FormError(v-if="nameSearchInvalid && !firstName",message="Required for name search.")
            .col-lg
              .form-group
                label(for="middleName") Middle Name
                input.form-control(name="middleName",id="middleName",v-model="middleName")
          .form-row.mt-2
            .col-lg-3
              .form-group
                label(for="birthDt") Date of Birth
                Datepicker(
                  :input-class="{'form-control': true}"
                  v-model="birthDt")
                small.form-text.text-muted MM/DD/YYYY
        Expander.mt-3(heading="Unique Identification Number",:show.sync="showUniqueId")
          .form-row
            .col-lg-6
              .form-group
                label(for="uniqueId") ID Number
                input.form-control(name="uniqueId",id="uniqueId",v-model="uniqueId")
        Expander.mt-3(heading="Address",:show.sync="showAddress")
          .form-row
            .col-lg-10
              .form-group
                label(for="address") Street Address
                input#address.form-control(
                  v-model="address"
                  :class="{'is-invalid': addressSearchInvalid && !address}"
                  @input="addressTouched = true")
                FormError(v-if="addressSearchInvalid && !address", message="Required for address search.")
            .col-lg
              .form-group
                label(for="suite") Apt/Suite
                input.form-control(name="suite",id="suite",v-model="suite")
          .form-row
            .col-lg
              .form-group
                label(for="city") City
                input#city.form-control(
                  v-model="city"
                  :class="{'is-invalid': addressSearchInvalid && !city}"
                  @input="cityTouched = true")
                FormError(v-if="addressSearchInvalid && !city", message="Required for address search.")
            .col-lg
              .form-group
                label(for="state") State
                CustomSelect(
                  v-model="state"
                  :options="states"
                  labelName="name"
                  valueName="abbreviation"
                  :invalid="addressSearchInvalid && !state"
                  @filter="addressStateFilter = $event"
                  @input="stateTouched = true")
                FormError(v-if="addressSearchInvalid && !state", message="Required for address search.")
            .col-lg
              .form-group
                label(for="zip") Zip
                input#zip.form-control(
                  v-model="zip"
                  @input="zipTouched = true")
        Expander.mt-3(heading="Locations",:show.sync="showLocations")
          CustomRadio(modelValue="all",v-model="locationSelect")
            span.body-1.ml-2 All States
          br
          CustomRadio(modelValue="select",v-model="locationSelect")
            span.body-1.ml-2 Select States
          SuperSelect.mt-3(taggable
            v-model="selectedLocations"
            :options="filteredStates"
            v-if="locationSelect === 'select'"
            @filter="stateFilter = $event"
            label-name="name")
        button.btn.btn-primary.mt-3(type="submit",@click.prevent="submitForm()") Search
</template>

<script>
import states from '../lib/states'
import { stateByName } from '@/lib/states'
import Expander from './Expander'
import SuperSelect from './SuperSelect'
import FormError from './FormError'
import FormErrorAlert from './FormErrorAlert'
import CustomRadio from './CustomRadio'
import CustomSelect from './CustomSelect'
import Datepicker from './Datepicker'
import * as validation from '@/lib/validation'
import { loadForm } from '@/lib/formstore'

export default {
  name: 'PersonSearchForm',
  data: function() {
    return {
      message: '',
      showError: false,
      states: states,
      purposeCode: '',
      lastName: '',
      firstName: '',
      middleName: '',
      birthDt: '',
      uniqueId: '',
      address: '',
      suite: '',
      city: '',
      state: '',
      zip: '',
      addressStateFilter: '',
      locationSelect: 'all',
      selectedLocations: [],
      stateFilter: '',
      ...validation.mapTouched(['purposeCode']),
      showPersonInfo: true,
      showUniqueId: false,
      showAddress: false,
      showLocations: false,
    }
  },
  mounted() {
    let formLoader = loadForm(
      'personsearch',
      'purposeCode',
      'lastName',
      'firstName',
      'middleName',
      'birthDt',
      'uniqueId',
      'address',
      'suite',
      'city',
      'state',
      'zip',
      'locationSelect',
      'selectedLocations',
      'showPersonInfo',
      'showUniqueId',
      'showAddress',
      'showLocations',
    )
    formLoader.bind(this)()
    if (this.uniqueId) this.showUniqueId = true
    if (this.address || this.suite || this.city || this.state || this.zip)
      this.showAddress = true
  },
  methods: {
    stateByName,
    submitForm: function() {
      if (this.formInvalid) {
        this.showError = true
        this.message =
          'There are missing fields. You must search using a combination of name, address or unique identifier. If searching for a name then first name and last name are required. If searching by address then street address, city and state are required.'
        window.scrollTo(0, 0)
      } else {
        this.$emit('personSearch', {
          purposeCode: this.purposeCode,
          lastName: this.lastName,
          firstName: this.firstName,
          middleName: this.middleName,
          birthDt: this.birthDt,
          idNbr: this.uniqueId,
          address1: this.address,
          address2: this.suite,
          city: this.city,
          stateCd: this.state,
          postalCode: this.zip,
          searchStates: this.searchStates,
          iceAgent: this.$store.state.account.iceAgent,
        })
      }
    },
    clearForm: function() {
      this.showError = false
      this.message = ''
      this.purposeCode = ''
      this.lastName = ''
      this.firstName = ''
      this.middleName = ''
      this.birthDt = ''
      this.uniqueId = ''
      this.address = ''
      this.suite = ''
      this.city = ''
      this.state = ''
      this.zip = ''
      this.locationSelect = 'all'
      this.selectedLocations = []
      this.stateFilter = ''
    },
  },
  computed: {
    stateAbbr() {
      const st = this.stateByName(this.state)
      return st ? st.abbreviation : ''
    },
    nameSearch() {
      return !!(this.firstName || this.lastName)
    },
    addressSearch() {
      return !!(this.address || this.city || this.state || this.zip)
    },
    nameSearchEmpty() {
      return !this.firstName || !this.lastName
    },
    nameSearchInvalid() {
      return this.nameSearchEmpty && this.nameSearch
    },
    addressSearchEmpty() {
      return !this.address || !this.city || !this.state
    },
    addressSearchInvalid() {
      return this.addressSearchEmpty && this.addressSearch
    },
    formInvalid() {
      if (!this.uniqueId && this.nameSearchEmpty && this.addressSearchEmpty)
        return true
      if (!this.purposeCode) return true
      return false
    },
    searchStates() {
      return this.locationSelect === 'all'
        ? []
        : this.selectedLocations.map(v => v.abbreviation)
    },
    filteredStates() {
      return this.states.filter(st =>
        st.name.toLowerCase().includes(this.stateFilter),
      )
    },
    addressFilteredStates() {
      if (this.addressStateFilter === '') return this.states
      return this.states.name.filter(st =>
        st.toLowerCase().includes(this.addressStateFilter.toLowerCase()),
      )
    },
    ...validation.mapRequired(['purposeCode']),
  },
  components: {
    Expander,
    SuperSelect,
    FormError,
    FormErrorAlert,
    CustomRadio,
    CustomSelect,
    Datepicker,
  },
}
</script>

<style lang="scss" scoped>
@import '../assets/styles/bodies';
.separator {
  margin-top: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #d8d8d8;
}
.red {
  color: red;
}
label {
  @extend .body-1;
}

.form-text {
  font-size: 0.75rem;
}
</style>
